import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, PageLayout, sentinel } from '@sentinel/ui';
import { takeResumedResult } from '@sentinel/api-client';
import { ConfirmDialog } from '../features/security/components/confirm-dialog';
import { ApiKeyList } from '../features/credentials/components/api-key-list';
import { CreateKeyDialog } from '../features/credentials/components/create-key-dialog';
import { RevealCard } from '../features/credentials/components/reveal-card';
import {
  type ApiKeyItem,
  type CreateApiKeyResult,
  useApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
  useRotateApiKey,
} from '../features/credentials/use-api-keys';
import { asCreatedKey } from '../features/credentials/resumed-reveal';

const headerStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '0.25rem',
};
const sectionStyle: React.CSSProperties = { display: 'grid', gap: '0.75rem' };
const emptyStyle: React.CSSProperties = {
  display: 'grid',
  gap: '0.5rem',
  justifyItems: 'center',
  textAlign: 'center',
  padding: '2.5rem 1rem',
  borderRadius: '0.75rem',
  border: `1px solid ${sentinel.borderDefault}`,
  background: sentinel.surfaceSunken,
};

function getStatus(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}
function getErrorMessage(e: unknown): string | undefined {
  const body = (e as { body?: { error?: string } } | null)?.body;
  return body?.error;
}

export function CredentialsPage() {
  const { t } = useTranslation('credentials');
  const list = useApiKeys();
  const createKey = useCreateApiKey();
  const rotateKey = useRotateApiKey();
  const revokeKey = useRevokeApiKey();

  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<CreateApiKeyResult | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyItem | null>(null);
  const [rotateTarget, setRotateTarget] = useState<ApiKeyItem | null>(null);
  const [topError, setTopError] = useState<string | null>(null);
  const [topSuccess, setTopSuccess] = useState<string | null>(null);

  function clearBanners() {
    setTopError(null);
    setTopSuccess(null);
  }

  // Federated (SSO) step-up is a full-page redirect, so a create/rotate begun
  // before step-up cannot resolve through its mutation. The resume hook replays
  // the request after returning from the IdP and publishes the response to an
  // in-memory channel. Pick it up here — on mount (the event may have fired
  // before this page mounted) AND on the event — and reveal the one-time secret.
  useEffect(() => {
    const consume = () => {
      const result = takeResumedResult();
      if (!result) return;
      const created = asCreatedKey(result);
      if (created) {
        setRevealed(created);
        setTopSuccess(t('created_success'));
      }
      // The federated path never ran the mutation's onSuccess, so refresh the
      // list explicitly (covers create, rotate, and revoke).
      void list.refetch();
    };
    consume();
    window.addEventListener('sentinel:step-up-resumed', consume);
    return () => window.removeEventListener('sentinel:step-up-resumed', consume);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCreate(label: string | null) {
    setCreateError(null);
    clearBanners();
    try {
      const result = await createKey.mutateAsync({ label });
      setCreateOpen(false);
      setRevealed(result);
      setTopSuccess(t('created_success'));
    } catch (e) {
      const status = getStatus(e);
      // Step-up middleware handles 403 step_up_required transparently. If the
      // user cancels step-up the call rejects with 403 — leave the dialog
      // open so they can retry.
      if (status === 403) return;
      if (status === 400) {
        const msg = getErrorMessage(e);
        setCreateError(
          msg && msg.toLowerCase().includes('quota')
            ? t('quota_exceeded')
            : t('generic_error'),
        );
        return;
      }
      setCreateError(t('generic_error'));
    }
  }

  async function handleConfirmRevoke() {
    if (!revokeTarget) return;
    clearBanners();
    try {
      await revokeKey.mutateAsync({ id: revokeTarget.id });
      setRevokeTarget(null);
      setTopSuccess(t('revoked_success'));
    } catch (e) {
      const status = getStatus(e);
      if (status === 403) {
        setRevokeTarget(null);
        return;
      }
      setRevokeTarget(null);
      setTopError(t('generic_error'));
    }
  }

  async function handleConfirmRotate() {
    if (!rotateTarget) return;
    clearBanners();
    try {
      const result = await rotateKey.mutateAsync({ id: rotateTarget.id });
      setRotateTarget(null);
      setRevealed(result);
      setTopSuccess(t('rotated_success'));
    } catch (e) {
      const status = getStatus(e);
      if (status === 403) {
        setRotateTarget(null);
        return;
      }
      setRotateTarget(null);
      setTopError(t('generic_error'));
    }
  }

  const items = list.items;

  return (
    <>
      <PageLayout title={t('title')} subtitle={t('subtitle')}>
        {topError ? (
          <Alert type="error" role="alert">
            {topError}
          </Alert>
        ) : null}
        {topSuccess ? (
          <Alert type="info" role="status">
            {topSuccess}
          </Alert>
        ) : null}

        <section style={sectionStyle} data-testid="api-keys-section">
          <div style={headerStyle}>
            <h2 style={{ margin: 0 }}>{t('title')}</h2>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => {
                clearBanners();
                setCreateError(null);
                setCreateOpen(true);
              }}
              data-testid="api-keys-create-button"
            >
              {t('create_button')}
            </Button>
          </div>

          {revealed ? (
            <RevealCard
              secret={revealed.key}
              label={revealed.label}
              onDismiss={() => setRevealed(null)}
            />
          ) : null}

          {list.isLoading ? (
            <p>{t('loading')}</p>
          ) : list.error ? (
            <Alert type="error" role="alert">
              {t('generic_error')}
            </Alert>
          ) : items.length === 0 ? (
            <div style={emptyStyle} data-testid="api-keys-empty">
              <strong>{t('empty_title')}</strong>
              <p
                style={{
                  margin: 0,
                  color: sentinel.textMuted,
                  maxWidth: '40ch',
                }}
              >
                {t('empty_body')}
              </p>
            </div>
          ) : (
            <>
              <ApiKeyList
                items={items}
                busyId={
                  revokeKey.isPending
                    ? (revokeTarget?.id ?? null)
                    : rotateKey.isPending
                      ? (rotateTarget?.id ?? null)
                      : null
                }
                onRotate={(k) => {
                  clearBanners();
                  setRotateTarget(k);
                }}
                onRevoke={(k) => {
                  clearBanners();
                  setRevokeTarget(k);
                }}
              />
              {list.hasNextPage ? (
                <Button
                  type="button"
                  variant="secondary"
                  data-testid="api-keys-load-more"
                  disabled={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                >
                  {t('load_more')}
                </Button>
              ) : null}
            </>
          )}
        </section>
      </PageLayout>

      <CreateKeyDialog
        open={createOpen}
        submitting={createKey.isPending}
        inlineError={createError}
        onSubmit={handleCreate}
        onCancel={() => {
          setCreateOpen(false);
          setCreateError(null);
        }}
      />

      <ConfirmDialog
        open={Boolean(revokeTarget)}
        title={t('revoke_confirm_title')}
        subtitle={t('revoke_confirm_subtitle')}
        submitLabel={t('revoke_confirm_submit')}
        cancelLabel={t('revoke_confirm_cancel')}
        submitting={revokeKey.isPending}
        variant="danger"
        onSubmit={handleConfirmRevoke}
        onCancel={() => setRevokeTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(rotateTarget)}
        title={t('rotate_confirm_title')}
        subtitle={t('rotate_confirm_subtitle')}
        submitLabel={t('rotate_confirm_submit')}
        cancelLabel={t('rotate_confirm_cancel')}
        submitting={rotateKey.isPending}
        onSubmit={handleConfirmRotate}
        onCancel={() => setRotateTarget(null)}
      />
    </>
  );
}

export const Route = createFileRoute('/account/credentials')({
  component: CredentialsPage,
});
